/**
 * The shapes a ranked bar chart is drawn from.
 *
 * ==========================================================================================
 * THE SCALE IS THE WHOLE ARGUMENT
 * ==========================================================================================
 *
 * `dashboard.md` refuses a pie and a donut for the expense categories, and the reason is not taste:
 *
 *   "The parts are not exhaustive, so no part-of-whole mark can be honest about them."
 *
 * The rows are the ten largest categories out of up to seventeen, with no "Other" bucket and no
 * remainder row. Any mark that divides a whole — a pie, a donut, a treemap, a 100% stacked bar — states
 * that the parts shown ARE the whole, and they are not. `expense-summary-card.tsx` went as far as
 * refusing a percentage in the row, because a proportion of a whole the rows do not constitute is a
 * claim the reader cannot check.
 *
 * A ranked bar escapes that, and only if it is scaled correctly. Bars drawn against the SUM are a
 * part-of-whole mark wearing a different shape — each bar's length is then implicitly a share, and the
 * shares visibly fail to fill the axis. Bars drawn against the LARGEST VALUE make a different and true
 * statement: this one is the biggest, that one is about half of it, this one barely registers. No total
 * is asserted, so none can be wrong.
 *
 * That is why `RANKED_DOMAIN` is what it is and why this comment is here rather than in the impl: the
 * one line that could quietly turn an honest chart into a dishonest one is `domain`, and it does not
 * look like a decision.
 */

export interface RankedBar {
  /** Stable and unique — a category value, an id. Not the index. */
  key: string;
  label: string;
  /**
   * NON-NEGATIVE. A ranking of magnitudes; there is no meaning to a negative expense category here,
   * and a mixed-sign ranking would need a different mark and a zero line.
   */
  value: number;
}

/**
 * The x-domain: zero to the largest bar, never zero to the sum.
 *
 * `dataMax` is Recharts' own token for "the largest value in the data", so the axis fits the ranking
 * rather than a total nobody supplied.
 */
export const RANKED_DOMAIN = [0, "dataMax"] as const;

/**
 * Descending, so the chart is a ranking rather than a list that happens to be sorted.
 *
 * Ties break on the label, and that matters more than it looks: two categories at the same figure
 * would otherwise swap places between renders depending on the read's row order, and a chart whose
 * rows move when nothing changed reads as a bug in the figures. `localeCompare` is the same tiebreak
 * `summariseExpenses` already applies server-side, so the two agree.
 */
export function rankedBars(bars: readonly RankedBar[]): RankedBar[] {
  return [...bars].sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
}

/**
 * How tall the chart has to be to fit its rows.
 *
 * A horizontal bar chart's height is a function of its ROW COUNT, which is the opposite of every other
 * chart here — those are given a height and fit their data into it. Ten categories in 280px gives each
 * row 28px including its gap, and the label beside it is 12px type: legible, and the tick renderer
 * starts dropping labels below about 24px, which is silent. So the height is computed and the caller
 * does not choose it.
 */
export function rankedHeight(rows: number): number {
  const PER_ROW = 32;
  const CHROME = 24;
  return Math.max(rows, 1) * PER_ROW + CHROME;
}

/** Nothing to draw: no bars, or every value zero. */
export function isEmptyRanked(bars: readonly RankedBar[]): boolean {
  return bars.length === 0 || bars.every((bar) => bar.value === 0);
}
